import { useSmoothedValue } from '@hotpot-arcade/fanforge-meta-client';
import { DiscordActivityPlatformApi, Platform, usePlatform } from '@tofu/platform';
import { useEffect, useState } from 'react';
import FanforgeLogo from '../components/icons/fanforge-logo';

type LoadingVariant = 'fanforge' | 'mafia';

// Short, theme-accurate how-to-play tips shown while the standalone Mafia
// activity boots. Kept high-level (no exact numbers) so they stay correct as
// the ruleset evolves.
const MAFIA_TIPS = [
  'Wolves hunt at night. The village votes by day.',
  'Trust no one — anyone at the table could be a wolf.',
  'Watch who deflects, and who is quick to accuse.',
  'Your role is secret. Play it close.',
  'Survive the night. Unmask the wolves.',
];

// Community Discord for feedback / feature requests, surfaced on the Mafia loader.
const DISCORD_SUPPORT_INVITE = 'https://discord.gg/FaaeG4PTvq';
// Marketing site (roles reference, how-to-play), surfaced on the Mafia loader.
const WEBSITE_URL = 'https://www.whocriedwolf.gg/';

export function LoadingPage({ variant = 'fanforge' }: { variant?: LoadingVariant }) {
  const progress = useFakeProgressChunking();
  const movingProgress = useMovingProgress(progress, 10);
  const smoothedProgress = useSmoothedValue(movingProgress, 500);
  const isMafia = variant === 'mafia';

  return (
    <div className="h-full flex flex-col justify-center items-center gap-8 bg-[radial-gradient(50%_50%_at_50%_50%,#212124_0%,#141416_100%)]">
      {isMafia ? (
        <div className="flex flex-col items-center gap-1 text-center">
          <span className="text-4xl font-black tracking-wide text-white">So Who Cried Wolf?</span>
          <span className="text-sm font-medium uppercase tracking-[0.2em] text-dark-1">A Mafia Game</span>
        </div>
      ) : (
        <FanforgeLogo width="200px" height="50px" />
      )}
      <div className="p-3 max-w-[360px] md:max-w-[460px] w-full flex justify-center items-center border border-dark backdrop-blur-sm shadow-[0_0_16px_0_rgba(0,0,0,0.10)] rounded-2xl">
        <div className="w-full bg-customdarkgray h-1 self-stretch rounded-2xl">
          <div
            className="rounded-2xl h-full bg-[linear-gradient(90deg,#1AB8FF_9.57%,#E2FF70_51.38%,#FF59E1_93.18%)]"
            style={{ width: `${smoothedProgress}%` }}
          ></div>
        </div>
      </div>
      <div className="tracking-wide text-sm flex flex-row max-w-[360px] md:max-w-[460px] gap-1 w-screen text-center px-4">
        {isMafia ? (
          <MafiaTip />
        ) : (
          <p className="text-dark-1 font-bold">
            QuickTip:{' '}
            <span className="text-dark-1 font-normal">
              Type <span className="text-customgreen">/fanforge</span> in the Discord server to start playing with
              friends.
            </span>
          </p>
        )}
      </div>
      {isMafia && <MafiaNews />}
    </div>
  );
}

function MafiaNews() {
  const { platform } = usePlatform();

  // External links are sandboxed inside the Discord activity — route them through the
  // SDK. Fall back to a normal open for the web/browser build.
  const openExternal = (url: string) => {
    if (platform.platform === Platform.DiscordActivity) {
      void (platform as DiscordActivityPlatformApi).discordSdk.commands.openExternalLink({ url }).catch(() => {});
    } else {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  return (
    <div className="max-w-[360px] md:max-w-[460px] w-full px-4">
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dark bg-white/[0.03] p-4 text-center backdrop-blur-sm">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-customgreen">📣 Latest News</span>
        <p className="text-sm text-dark-1">
          Now on <span className="font-medium text-white">mobile</span>, with new{' '}
          <span className="font-medium text-white">Beginner</span> &amp;{' '}
          <span className="font-medium text-white">Standard</span> setups and a smoother night phase.
        </p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
          <button
            type="button"
            onClick={() => openExternal(WEBSITE_URL)}
            className="text-sm font-semibold text-customgreen hover:underline"
          >
            🐺 Roles &amp; guides at whocriedwolf.gg →
          </button>
          <button
            type="button"
            onClick={() => openExternal(DISCORD_SUPPORT_INVITE)}
            className="text-sm font-semibold text-customgreen hover:underline"
          >
            💬 Join our Discord →
          </button>
        </div>
      </div>
    </div>
  );
}

function MafiaTip() {
  const tip = useRotatingTip(MAFIA_TIPS, 3500);
  return (
    <p className="text-dark-1 font-bold w-full">
      Tip: <span className="text-dark-1 font-normal">{tip}</span>
    </p>
  );
}

const useRotatingTip = (tips: string[], intervalMs: number) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % tips.length), intervalMs);
    return () => clearInterval(id);
  }, [tips.length, intervalMs]);
  return tips[index];
};

const checkpoints = [10, 25, 50, 75, 85, 95];
const delay = 1000;
const useFakeProgressChunking = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let isActive = true;
    (async () => {
      for (const value of checkpoints) {
        await new Promise((r) => setTimeout(r, delay));
        if (isActive) setProgress(value);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);
  return progress;
};

const useMovingProgress = (actualProgress: number, duration: number = 10) => {
  const [progressThroughRemaining, setProgressThroughRemaining] = useState<number>(0); // starts at 0, moves to 1

  useEffect(() => {
    const frameDuration = 1000 / 60;
    const interval = setInterval(() => {
      setProgressThroughRemaining((prevProgress) => {
        const growthRate = 1 - Math.pow(0.5, frameDuration / (duration * 1000)); // log growth rate
        return Math.min(prevProgress + (1 - prevProgress) * growthRate, 1);
      });
    }, frameDuration);

    return () => clearInterval(interval);
  }, [duration]);

  return actualProgress + (100 - actualProgress) * progressThroughRemaining;
};
